import { classifyInstallationTypology, installationGuidance } from "@calcplan/ai-capabilities";
import type { CatalogItem } from "@calcplan/domain";

/**
 * The catalogue API returns the engineering tree needed by the editor to
 * instantiate a module. The model only needs its identity, dimensions and
 * resize contract; sending thousands of internal component nodes makes each
 * subsequent tool round unnecessarily expensive.
 *
 * The installation typology travels with the item on purpose: the planner
 * decides heights and gaps right after this search, so the professional
 * range must be in front of it as product data, not left to memory.
 */
export function catalogItemForAgent(item: CatalogItem): Record<string, unknown> {
  const typology = classifyInstallationTypology({
    name: item.name,
    category: [item.root, item.category].filter(Boolean).join(" / "),
    description: item.description,
    dimensionsMm: item.dimensionsMm,
  });
  const placementHint = typology.mount === "wall" ? "wall" : inferPlacementHint(item);
  const guidance = installationGuidance({
    target: { name: item.name, category: item.category, description: item.description, dimensionsMm: item.dimensionsMm },
    relation: null,
  });
  return {
    catalogItemId: item.stableItemId,
    id: item.id,
    stableItemId: item.stableItemId,
    revision: item.revision ?? "1.0.0",
    name: item.name,
    description: item.description,
    folderId: item.folderId,
    root: item.root,
    category: item.category,
    type: item.type,
    dimensionsMm: item.dimensionsMm,
    color: item.color,
    resize: item.resize,
    ...(placementHint ? { placementHint } : {}),
    ...(typology.kind !== "unknown"
      ? {
          installationTypology: {
            kind: typology.kind,
            label: typology.label,
            mount: typology.mount,
            confidence: typology.confidence,
            guidance: guidance.guidance,
          },
        }
      : {}),
    declaredInteractions: item.engineering?.dynamicAssetId
      ? "available_after_insertion"
      : "none_declared",
    availability: item.availability ?? "available",
  };
}

function inferPlacementHint(item: CatalogItem): "wall" | null {
  const text = [item.name, item.description, item.root, item.category]
    .filter(Boolean)
    .join(" ")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("pt-BR");
  return /\b(?:aereo|aereos|suspenso|suspensa|basculante)\b/.test(text) ? "wall" : null;
}
